import { memo } from "react";
import { PieChart, Pie, Cell, Tooltip as RTooltip, ResponsiveContainer } from "recharts";
import { PieChart as PieIcon } from "lucide-react";
import { CAT_LABEL, isToll } from "./budgetUtils";

const CAT_COLORS = {
  laden: "#2D6A4F",
  verblijf: "#3A6EA5",
  eten: "#E07A5F",
  tol: "#B5838D",
  parkeren: "#8E7DBE",
  tickets: "#52B788",
  overig: "#A8A29E",
};

export const CategoryPie = memo(({ extras, travelMode }) => {
  const sums = {};
  for (const e of extras || []) {
    const cat = isToll(e) ? "tol" : (e.category || "overig");
    sums[cat] = (sums[cat] || 0) + (e.amount_eur || 0);
  }
  const data = Object.entries(sums)
    .filter(([, v]) => v > 0.005)
    .sort((a, b) => b[1] - a[1])
    .map(([cat, value]) => ({
      cat,
      name: cat === "laden" && travelMode === "moto" ? "Brandstof" : (CAT_LABEL[cat] || cat),
      value: Math.round(value * 100) / 100,
      color: CAT_COLORS[cat] || CAT_COLORS.overig,
    }));
  if (data.length < 2) return null;
  const total = data.reduce((a, d) => a + d.value, 0);
  return (
    <div className="bg-card rounded-2xl p-5" data-testid="category-pie-card">
      <p className="font-heading font-bold text-sm flex items-center gap-1.5">
        <PieIcon className="w-4 h-4 text-[#3A6EA5]" /> Waar ging het geld naartoe?
      </p>
      <p className="text-[11px] text-muted-foreground mt-0.5">verdeling van je € {total.toFixed(2)} aan uitgaven per categorie</p>
      <div className="flex flex-col sm:flex-row items-center gap-2 mt-2">
        <div className="w-full sm:w-1/2 h-[190px] relative">
          <ResponsiveContainer width="100%" height="100%" debounce={200}>
            <PieChart>
              <Pie data={data} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85}
                paddingAngle={2} strokeWidth={0} isAnimationActive={false}>
                {data.map((d) => <Cell key={d.cat} fill={d.color} />)}
              </Pie>
              <RTooltip formatter={(v, name) => [`€ ${Number(v).toFixed(2)} (${Math.round((v / total) * 100)}%)`, name]}
                contentStyle={{ borderRadius: 12, fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="font-mono font-extrabold text-sm" data-testid="category-pie-total">€ {total.toFixed(0)}</span>
            <span className="text-[9px] uppercase tracking-wide text-muted-foreground">totaal</span>
          </div>
        </div>
        <div className="w-full sm:w-1/2 space-y-1" data-testid="category-pie-legend">
          {data.map((d) => (
            <div key={d.cat} data-testid={`category-pie-row-${d.cat}`} className="flex items-center gap-2 text-xs">
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: d.color }} />
              <span className="flex-1 min-w-0 truncate">{d.name}</span>
              <span className="font-mono font-semibold shrink-0">€ {d.value.toFixed(2)}</span>
              <span className="text-muted-foreground w-9 text-right shrink-0">{Math.round((d.value / total) * 100)}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
});
